import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ResourceCreatorService } from '../../../../services/resource-creator.service';
import { TemplateDetailsData } from '../../../../models/wizard-plugin-models/template-details-data';
import { UserAccountData } from '../../../../models/wizard-plugin-models/user-account-data';
import { ResourceMetaData } from '../../../../models/resource-meta-data';
import { Log, IShortLogger } from '../../../../services/logger.service';

@Component({
  selector: 'app-user-account-create-wizard',
  templateUrl: './user-account-create-wizard.component.html',
  styleUrl: './user-account-create-wizard.component.css',
})
export class UserAccountCreateWizardComponent implements OnInit {
  private readonly logger: IShortLogger<UserAccountCreateWizardComponent> =
    Log.create(UserAccountCreateWizardComponent);

  templateDetailsForm!: FormGroup;
  userAccountForm!: FormGroup;

  isSubmitting = false;

  readonly accessLevels: { id: string; name: string; description: string; icon: string }[] = [
    { id: 'read',  name: 'Read Only',      description: 'View and read access only',     icon: 'visibility' },
    { id: 'write', name: 'Read/Write',     description: 'Read and write access',          icon: 'edit' },
    { id: 'admin', name: 'Administrator',  description: 'Full administrative access',     icon: 'admin_panel_settings' },
  ];

  /** Azure role names keyed by access-level id. */
  readonly azureRoles: Record<string, string> = {
    read:  'Reader',
    write: 'Contributor',
    admin: 'Owner',
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly resourceCreatorService: ResourceCreatorService,
    readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.buildForms();
  }

  // ── Form builders ──────────────────────────────────────────────────────────

  private buildForms(): void {
    this.templateDetailsForm = this.fb.group({
      templateName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.\-]+$/)], [this.uniqueNameValidator()]],
      templateDescription: [''],
    });

    this.userAccountForm = this.fb.group({
      cloudProvider: ['azure', Validators.required],
      accessLevel:   ['',      Validators.required],
    });
  }

  /** Debounced async validator: checks that no existing template shares this name. */
  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const name = (control.value ?? '').trim();
      if (!name) return of(null);
      return timer(500).pipe(
        switchMap(() => this.resourceCreatorService.getAll()),
        map((resources: any[]) => {
          const taken = resources.some(r => {
            const existingName =
              (r.data?.['TemplateDetailsData']?.templateName ?? r.metaData?.name ?? '').trim();
            return existingName.toLowerCase() === name.toLowerCase();
          });
          return taken ? { nameTaken: true } : null;
        }),
      );
    };
  }

  // ── Access level helpers ───────────────────────────────────────────────────

  get selectedAccessLevelId(): string {
    return this.userAccountForm.get('accessLevel')?.value ?? '';
  }

  selectAccessLevel(id: string): void {
    this.userAccountForm.get('accessLevel')!.setValue(id);
    this.userAccountForm.get('accessLevel')!.markAsTouched();
  }

  get selectedAccessLevelLabel(): string {
    return this.accessLevels.find(l => l.id === this.selectedAccessLevelId)?.name ?? '';
  }

  // ── Review helpers ─────────────────────────────────────────────────────────

  get reviewTemplateName(): string {
    return (this.templateDetailsForm.value.templateName ?? '').trim();
  }

  get reviewTemplateDesc(): string {
    return (this.templateDetailsForm.value.templateDescription ?? '').trim();
  }

  get reviewAzureRole(): string {
    return this.azureRoles[this.selectedAccessLevelId] ?? '';
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.templateDetailsForm.invalid || this.userAccountForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    const templateData = new TemplateDetailsData();
    templateData.templateName = this.reviewTemplateName;
    templateData.templateDescription = this.reviewTemplateDesc;

    const userAccountData = new UserAccountData();
    userAccountData.selectedCloudProvider = this.userAccountForm.value.cloudProvider;
    userAccountData.selectedAccessLevel   = this.userAccountForm.value.accessLevel;

    const metaData = new ResourceMetaData();
    metaData.name        = templateData.templateName;
    metaData.type        = 'acc';
    metaData.description = templateData.templateDescription ?? '';

    // ResourceCreatorService.create() accepts (data, config | undefined).
    // We pass a minimal config-like object; the service only uses name/type/description.
    const config: any = {
      name:        metaData.name,
      type:        metaData.type,
      description: metaData.description,
      plugins:     [],
    };

    this.resourceCreatorService.create(
      { TemplateDetailsData: templateData, UserAccountData: userAccountData },
      config,
    ).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.logger.i('User account template created successfully');
        this.router.navigate(['/templates'], { queryParams: { tab: 'acc' } });
      },
      error: (err) => {
        this.isSubmitting = false;
        this.logger.e('Failed to create user account template', err);
        this.snackBar.open(
          'Failed to create user account template. Please try again.',
          'Dismiss',
          { duration: 5000 },
        );
      },
    });
  }
}
